/**
 * Club constants. These come from SPEC.md §1 and must not be edited casually —
 * the fees drive the whole dues engine and the distances drive bib colour.
 */

/*
 * DOMAIN VOCABULARY — the allowed values across the club data model.
 * (These were TypeScript unions in the original shared package. They are the
 * only values the rest of the app ever writes; anything else is a bug.)
 *
 *   tier          'annual' | 'student' | 'junior' | 'life'      — keys of TIERS
 *   distance      '5K' | '10K' | '21.1K' | '42.2K'              — keys of DISTS
 *   event kind    'run' | 'race' | 'meeting' | 'volunteer'      — keys of KIND_LABEL
 *   ground        'nanjappa' | 'chikanna' | 'collector'         — keys of GROUNDS
 *   member status 'pending' | 'active' | 'paused' | 'left'
 *                 — 'pending' is a committee-added member awaiting the committee's
 *                 final activation; they hold a bib but cannot sign in and are not
 *                 on the roster screens
 *   reset status  'active' | 'used' | 'void'                 — see reset.js
 *   member role   'member' | 'coach' | 'admin'   (portal role is only member|admin)
 *   gender        'M' | 'F' | 'X' | null
 *   signup status 'signed' | 'confirmed'
 *   payment       'razorpay' | 'upi' | 'cash' | 'bank'
 *   join request  'pending' | 'approved' | 'declined'
 *   message kind  'notice' | 'suggestion'
 *
 * CONVENTIONS, everywhere in the model:
 *   Dates are ISO `YYYY-MM-DD` strings. Times are integer seconds (`timeS`).
 *   Only audit fields (`ts`) are epoch milliseconds.
 *   `duesPaidTill: null` means never paid — it drives the whole dues engine.
 *   `phone` (E.164) and `email` (lowercased) are BOTH login identities, and
 *   both are unique across live members. `email` is mandatory on every form
 *   that creates a member — it is the only password-recovery channel.
 *   A receipt reads `TRC/2026-27/0001` and is never generated on the client.
 *   A member's emergency block (emName/emPhone/emRel/blood/medical/guardian*)
 *   is admin-visible only — SPEC.md §3.10. A member sees their own; never
 *   another member's.
 */

export const CLUB = {
  name: 'Tirupur Runners',
  established: 2013,
  city: 'Tirupur, Tamil Nadu',
};

/**
 * The committee account's mobile number, in E.164.
 *
 * This is the number that reaches the admin portal. It is a seed value, not a
 * permission: authority comes from `role: 'admin'` on the member record (and,
 * against the real backend, from a Firebase custom claim). Changing this
 * constant changes who the seeded committee account belongs to — it does not
 * grant anyone anything on its own.
 *
 * To add a second admin against the live backend:
 *   npm run grant-admin -- +91XXXXXXXXXX
 */
export const CLUB_ADMIN_PHONE = '+918197091531';

/**
 * The committee account's email — the second way into the admin portal, and
 * the address a committee password reset is emailed to. Same status as the
 * number above: a seed value, not a permission.
 */
export const CLUB_ADMIN_EMAIL = 'sankarkumar710@gmail.com';

/**
 * The seeded member address used for testing the real reset email end to end.
 * It belongs to a normal member — nothing about it grants anything. Kept next
 * to the admin address so the pair is obvious when the roster is rebuilt.
 */
export const TEST_MEMBER_EMAIL = 'sankarkit7@gmail.com';

/**
 * Where club email comes FROM.
 *
 * The club owns no domain yet (owner's direction, 29 Jul 2026 — do not use
 * `tirupurrunners.club`), so mail goes out through SendGrid's **Single Sender
 * Verification**: one address, verified by clicking a link, and then any
 * recipient is allowed. That is the whole reason the club is on SendGrid and
 * not Resend, whose unverified mode can only ever reach the account owner.
 *
 * The cost of the shortcut: this is a `gmail.com` address, and `gmail.com`
 * publishes a strict DMARC policy that mail relayed by SendGrid cannot satisfy.
 * Receivers will often file it as spam. Acceptable while testing; NOT
 * acceptable for mailing 250 members about money and race day.
 *
 * The fix is a domain. Verify one in SendGrid (Sender Authentication), then set
 * MAIL_FROM in apps/api/.env and this constant together — SPF and DKIM then
 * align and the spam problem goes away.
 */
export const CLUB_MAIL_FROM = `Tirupur Runners <${CLUB_ADMIN_EMAIL}>`;
export const CLUB_MAIL_REPLY_TO = CLUB_ADMIN_EMAIL;

/** Home grounds — a member belongs to one. SPEC.md §1. */
export const GROUNDS = {
  nanjappa: 'Nanjappa School Grounds',
  chikanna: 'Chikanna College Grounds',
  collector: 'Tirupur Collector Office',
};

/** Dues tiers. ₹1500/yr annual · ₹750 student & junior · ₹15,000 life. */
/* Each entry is { label, fee, months } — months null = never expires (life). */
export const TIERS = {
  annual: { label: 'Annual', fee: 1500, months: 12 },
  student: { label: 'Student', fee: 750, months: 12 },
  junior: { label: 'Junior', fee: 750, months: 12 },
  life: { label: 'Life', fee: 15000, months: null },
};

/** The distance ladder. Caps at half — 42.2K is external and rare. */
/* Each entry is { key, km, cls } — cls is the CSS class colouring a bib's top rule. */
export const DISTS = [
  { key: '5K', km: 5, cls: 't5' },
  { key: '10K', km: 10, cls: 't10' },
  { key: '21.1K', km: 21.1, cls: 't21' },
  { key: '42.2K', km: 42.2, cls: 't42' },
];

/** Volunteer hours the club expects from each member per year. */
export const EXPECT_VOL = 8;

/**
 * Days before expiry at which dues status becomes "soon" — and, per SPEC.md §4,
 * the same window in which the payment CTA appears. One constant, so the pill
 * and the button can never disagree about whether money is owed.
 */
export const SOON_WINDOW = 30;

/*
 * How close to expiry the renewal button becomes USABLE. Owner's direction,
 * 16 Aug 2026: a paid member should still see the button — so it is obvious
 * that renewal exists and where it will be — but should not be able to press
 * it two months early.
 *
 * Deliberately narrower than SOON_WINDOW, which stays at 30 so the "N days
 * left" warning still appears a month out. The warning and the button are
 * different jobs: one tells you it is coming, the other lets you act.
 */
export const PAYABLE_WINDOW = 7;

export const KIND_LABEL = {
  run: 'Group run',
  race: 'Race',
  meeting: 'Meeting',
  volunteer: 'Volunteer duty',
};

/** Maps an event kind to the modifier class on `.ev` / `.edet`. */
export const KIND_CLASS = {
  run: '',
  race: 'race',
  meeting: 'meet',
  volunteer: 'vol',
};

/**
 * The password every SEEDED member starts with.
 *
 * A real member sets their own during join onboarding, or via an emailed reset
 * link; against the live backend passwords live in Firebase Auth, never in this
 * repo. This is a starting value for the fixture roster and nothing more — it
 * is deliberately NOT shown on the sign-in screen any more (owner's direction,
 * 29 Jul 2026): the club now behaves like a real app, and a real app does not
 * print a password on its front door.
 */
export const DEMO_PASSWORD = 'run@2026';

/**
 * The public support address, shown in the marketing footer.
 *
 * A constant rather than an env var on purpose: it is public, identical in
 * every environment, and adding a `VITE_` value would mean one more thing to
 * type into the Render dashboard for a string that never varies. One place is
 * the point — not scattered `mailto:` literals.
 *
 * REQUIRES AN MX RECORD ON runnercrew.in. Verifying the domain with Resend lets
 * the platform SEND as any address on it; it creates no mailbox to RECEIVE, and
 * the `send.runnercrew.in` MX that Resend asked for is bounce handling for
 * outbound mail, not an inbox. Until forwarding exists, mail here bounces:
 *
 *     dig +short MX runnercrew.in @8.8.8.8      # must not be empty
 */
export const SUPPORT_EMAIL = 'support@runnercrew.in';
